import {
  ODSAdmin,
  ODSCompany,
  ODSCompliance,
  ODSId,
  ODSNotification,
  ODSSecurity,
  ODSServer,
  ODSUser,
} from '@telekom-ods/react-icons';
import useHasUnreadNotifications from 'notificationMicrofrontend/useHasUnreadNotifications';
import { useCallback, useEffect, useMemo } from 'react';
import type { IntlShape } from 'react-intl';
import { useDispatch } from 'react-redux';
import { matchPath, type NavigateFunction } from 'react-router';

import { fetchOrganizations } from '../../../redux/organizations/actions';

import useSeeChangeCloudPwd from './useSeeChangeCloudPwd';
import type { LayoutPermissions } from './useSideNavItems.types';

import type { Organization } from '@/types/organization';

interface UseSideNavItemsProps {
  intl: IntlShape;
  permissions: LayoutPermissions;
  navigate: NavigateFunction;
  pathname: string;
  selectedOrganization: Organization | null;
  ownOrganizationData: Organization | null;
  isAuthenticated: boolean | undefined;
  isUserDataLoaded?: boolean;
}

const useSideNavItems = ({
  intl,
  permissions,
  navigate,
  pathname,
  selectedOrganization,
  ownOrganizationData,
  isAuthenticated,
  isUserDataLoaded = true,
}: UseSideNavItemsProps) => {
  const hasUnreadNotifications = useHasUnreadNotifications();
  const dispatch = useDispatch();

  const canSeeChangeCloudPwd: boolean = useSeeChangeCloudPwd();

  const organization = permissions.canSeeOwnOrganization
    ? ownOrganizationData
    : selectedOrganization;

  useEffect(() => {
    if (permissions.canSeeOrganizations) {
      dispatch(fetchOrganizations());
    }
  }, [dispatch, permissions.canSeeOrganizations]);

  // The "own organization" collapsible manages its own expand/collapse
  // animation internally via a ref it keeps on the subItems container.
  // ODSSideNavigationItem spreads unrecognized rest props (our
  // `data-own-organization-toggle` marker included) onto its own outer
  // <li>, not onto the clickable trigger - the actual button is a
  // descendant tagged `data-nav-trigger`. Passing our own `ref` through
  // the item (to reach that button) would also silently detach the
  // library's own ref from the subitems container, breaking its height
  // animation. A plain DOM attribute has no such side effect, so we
  // locate the button that way instead of via a React ref.
  useEffect(() => {
    if (!organization?._id) {
      return;
    }

    let frameId1: number | undefined;
    let frameId2: number | undefined;

    // Defer the click two frames: the library keeps its own copy of
    // subItems, populated from props by a separate effect that flushes
    // after paint. Our MutationObserver callback is a microtask that can
    // fire as soon as the button first appears — before that effect has
    // run — so the DOM genuinely has no subItems yet at that instant.
    // Clicking then makes the library's own scrollHeight measurement
    // correctly read 0 (there's nothing to measure), and it gets stuck
    // mid-animation since no visible height change means no transitionend
    // ever fires to finish the job. Waiting a couple of frames lets that
    // effect populate the list first.
    const clickAfterSubItemsSettle = (button: HTMLElement): void => {
      frameId1 = requestAnimationFrame(() => {
        frameId2 = requestAnimationFrame(() => {
          if (button.getAttribute('aria-expanded') !== 'true') {
            button.click();
          }
        });
      });
    };

    const tryOpen = (): boolean => {
      const button = document.querySelector<HTMLElement>(
        '[data-own-organization-toggle] [data-nav-trigger]'
      );
      if (button === null) {
        return false;
      }
      if (button.getAttribute('aria-expanded') !== 'true') {
        clickAfterSubItemsSettle(button);
      }
      return true;
    };

    if (tryOpen()) {
      return () => {
        if (frameId1 !== undefined) cancelAnimationFrame(frameId1);
        if (frameId2 !== undefined) cancelAnimationFrame(frameId2);
      };
    }

    const observer = new MutationObserver(() => {
      if (tryOpen()) {
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (frameId1 !== undefined) cancelAnimationFrame(frameId1);
      if (frameId2 !== undefined) cancelAnimationFrame(frameId2);
    };
  }, [organization?._id]);

  const goTo = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  // Organizations
  const organizationMenus = useMemo(
    () => [
      isAuthenticated &&
        isUserDataLoaded &&
        !ownOrganizationData?._id && {
          label: intl.formatMessage({ id: 'onboarding' }),
          icon: <ODSAdmin />,
          active: Boolean(matchPath({ path: '/onboarding/welcome' }, pathname)),
          onClick: () => {
            goTo('/onboarding/welcome');
          },
        },
      permissions.canSeeOrganizations &&
        ownOrganizationData?._id && {
          label: intl.formatMessage({ id: 'governance' }),
          icon: <ODSCompany />,
          active: Boolean(matchPath({ path: '/governance/*' }, pathname)),
          onClick: () => {
            goTo('/governance/organizations');
          },
        },
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      ((permissions.canSeeOrganizations && selectedOrganization) ||
        (permissions.canSeeOwnOrganization && ownOrganizationData?._id)) && {
        icon: <ODSCompany />,
        'data-own-organization-toggle': true,
        label: organization?.enterpriseName,
        active: Boolean(
          matchPath({ path: `/governance/organizations/${organization?._id ?? ''}` }, pathname)
        ),
        items: [
          {
            label: intl.formatMessage({ id: 'overview' }),
            active: Boolean(
              matchPath({ path: `/governance/organizations/${organization?._id ?? ''}` }, pathname)
            ),
            onClick: () => {
              goTo(`/governance/organizations/${organization?._id ?? ''}`);
            },
          },
          permissions.canSeeUsers && {
            label: intl.formatMessage({ id: 'users' }),
            onClick: () => {
              goTo(`/governance/organizations/${organization?._id ?? ''}/users`);
            },
            active: Boolean(
              matchPath(
                { path: `/governance/organizations/${organization?._id ?? ''}/users` },
                pathname
              )
            ),
          },
          permissions.canSeeProjects && {
            label: intl.formatMessage({ id: 'projects' }),
            onClick: () => {
              goTo(`/governance/organizations/${organization?._id ?? ''}/projects`);
            },
            active: Boolean(
              matchPath(
                { path: `/governance/organizations/${organization?._id ?? ''}/projects` },
                pathname
              )
            ),
          },
          permissions.canSeeOrganizationProducts && {
            label: intl.formatMessage({ id: 'products' }),
            onClick: () => {
              goTo(`/governance/organizations/${organization?._id ?? ''}/products`);
            },
            active: Boolean(
              matchPath(
                { path: `/governance/organizations/${organization?._id ?? ''}/products` },
                pathname
              )
            ),
          },
        ].filter(Boolean),
      },
    ],
    [
      isAuthenticated,
      isUserDataLoaded,
      ownOrganizationData?._id,
      intl,
      pathname,
      permissions.canSeeOrganizations,
      permissions.canSeeOwnOrganization,
      permissions.canSeeUsers,
      permissions.canSeeProjects,
      permissions.canSeeOrganizationProducts,
      selectedOrganization,
      organization?.enterpriseName,
      organization?._id,
      goTo,
    ]
  );

  const productsMenu = useMemo(
    () => [
      // List of products
      // Compute
      isAuthenticated &&
        permissions.canSeeProducts && {
          label: intl.formatMessage({ id: 'compute' }),
          icon: <ODSCompliance />,
          items: [
            (permissions.hasComputeActivated || permissions.hasEvaluationActivated) && {
              label: intl.formatMessage({ id: 'myProducts' }),
              active: Boolean(matchPath({ path: '/products/compute' }, pathname)),
              onClick: () => {
                goTo('/products/compute');
              },
            },
            {
              label: intl.formatMessage({ id: 'productSpecification' }),
              active: Boolean(matchPath({ path: '/products/compute/specification' }, pathname)),
              onClick: () => {
                goTo('/products/compute/specification');
              },
            },
            permissions.canOrderCompute && {
              onClick: () => {
                goTo('/products/compute/order');
              },
              active: Boolean(matchPath({ path: '/products/compute/order' }, pathname)),
              label: intl.formatMessage({ id: 'order' }),
            },
            permissions.hasEvaluationActivated && {
              onClick: () => {
                goTo('/products/compute/free-trial');
              },
              active: Boolean(matchPath({ path: '/products/compute/free-trial' }, pathname)),
              label: intl.formatMessage({ id: 'freeTrial' }),
            },
          ].filter(Boolean),
        },
      // CAAS
      isAuthenticated &&
        permissions.canSeeProducts && {
          label: intl.formatMessage({ id: 'caas' }),
          icon: <ODSCompliance />,
          items: [
            permissions.hasCaasActivated && {
              label: intl.formatMessage({ id: 'myClusters' }),
              active: Boolean(matchPath({ path: '/products/caas-clusters' }, pathname)),
              onClick: () => {
                goTo('/products/caas-clusters');
              },
            },
            permissions.hasCaasActivated && {
              label: intl.formatMessage({ id: 'myProducts' }),
              active: Boolean(matchPath({ path: '/products/caas-on-openstack' }, pathname)),
              onClick: () => {
                goTo('/products/caas-on-openstack');
              },
            },
            {
              label: intl.formatMessage({ id: 'productSpecification' }),
              active: Boolean(matchPath({ path: '/products/caas/specification' }, pathname)),
              onClick: () => {
                goTo('/products/caas/specification');
              },
            },
          ].filter(Boolean),
        },
    ],
    [
      isAuthenticated,
      permissions.canSeeProducts,
      permissions.hasComputeActivated,
      permissions.hasEvaluationActivated,
      permissions.canOrderCompute,
      permissions.hasCaasActivated,
      intl,
      pathname,
      goTo,
    ]
  );

  return useMemo(
    () =>
      [
        ...organizationMenus,
        ...productsMenu,
        permissions.canSeeReports && {
          label: intl.formatMessage({ id: 'reports' }),
          icon: <ODSCompliance />,
          items: [
            {
              label: intl.formatMessage({ id: 'juiceGroups' }),
              active: Boolean(matchPath({ path: '/reports/juicegroups' }, pathname)),
              onClick: () => {
                goTo('/reports/juicegroups');
              },
            },
            {
              label: intl.formatMessage({ id: 'caasCluster' }),
              active: Boolean(matchPath({ path: '/reports/caas-clusters' }, pathname)),
              onClick: () => {
                goTo('/reports/caas-clusters');
              },
            },
          ].filter(Boolean),
        },
        !isAuthenticated && {
          label: intl.formatMessage({ id: 'products' }),
          icon: <ODSServer />,

          items: [
            {
              label: intl.formatMessage({ id: 'compute' }),
              active: pathname.startsWith('/products/compute/specification'),
              onClick: () => {
                goTo('/products/compute/specification');
              },
            },
            {
              label: intl.formatMessage({ id: 'caas' }),
              active: pathname.startsWith('/products/caas/specification'),
              onClick: () => {
                goTo('/products/caas/specification');
              },
            },
          ].filter(Boolean),
        },
        isAuthenticated &&
          ownOrganizationData?._id &&
          canSeeChangeCloudPwd && {
            label: intl.formatMessage({ id: 'security' }),
            onClick: () => {
              goTo('/account/security');
            },
            active: Boolean(matchPath({ path: '/account/security' }, pathname)),
            icon: <ODSSecurity />,
          },
        isAuthenticated &&
          ownOrganizationData?._id &&
          permissions.canSeeOnboardingUsers && {
            label: intl.formatMessage({ id: 'onboardingUsers' }),
            onClick: () => {
              goTo('/onboarding/users');
            },
            active: Boolean(matchPath({ path: '/onboarding/users' }, pathname)),
            icon: <ODSId />,
          },
        isAuthenticated && {
          label: intl.formatMessage({ id: 'notifications' }),
          onClick: () => {
            goTo('/account/notifications');
          },
          active: Boolean(matchPath({ path: '/account/notifications' }, pathname)),
          icon: <ODSNotification />,
          showBadge: hasUnreadNotifications,
        },
        isAuthenticated && {
          label: intl.formatMessage({ id: 'profile' }),
          onClick: () => {
            goTo('/account');
          },
          active: Boolean(matchPath({ path: '/account', end: true }, pathname)),
          icon: <ODSUser />,
        },
      ].filter(Boolean),
    [
      ownOrganizationData?._id,
      organizationMenus,
      productsMenu,
      permissions.canSeeReports,
      permissions.canSeeOnboardingUsers,
      intl,
      pathname,
      isAuthenticated,
      canSeeChangeCloudPwd,
      hasUnreadNotifications,
      goTo,
    ]
  );
};

export default useSideNavItems;
